import { Automation, AutomationVersionSimple } from '@/types/automations.ts';
import { ManualTriggerInput, SubAutomationTriggerInputMapping } from '@/types/automationSteps.ts';
import { DEFAULT_DYNAMIC_DATA_INPUT } from '@/utils/constants.ts';

export function buildSubAutomationTargetOptions(
  automations: Automation[] | undefined,
  currentAutomationId: string,
  currentTargetAutomationId?: string | null,
) {
  return (automations ?? [])
    .filter((automation) => automation.id !== currentAutomationId)
    .filter((automation) => !automation.retired_at || automation.id === currentTargetAutomationId)
    .map((automation) =>
      automation.retired_at
        ? {
            value: automation.id,
            label: `${automation.name} (retired; current pin)`,
            disabled: true,
          }
        : { value: automation.id, label: automation.name },
    )
    .sort((left, right) => left.label.localeCompare(right.label));
}

/** Keep a fetched historical pin visible when pagination has not reached it. */
export function buildSubAutomationVersionOptions(
  versions: Array<
    Pick<AutomationVersionSimple, 'id' | 'version_number' | 'published_at'> & { isPublished?: boolean }
  >,
  currentPinnedVersionId: string | null,
  selectedVersion?: Pick<AutomationVersionSimple, 'id' | 'version_number' | 'published_at'> & {
    isPublished?: boolean;
  },
) {
  const versionsById = new Map(versions.map((version) => [version.id, version]));
  if (selectedVersion && !versionsById.has(selectedVersion.id)) {
    versionsById.set(selectedVersion.id, selectedVersion);
  }

  return [...versionsById.values()]
    .sort(
      (left, right) =>
        (right.version_number ?? Number.NEGATIVE_INFINITY) -
          (left.version_number ?? Number.NEGATIVE_INFINITY) || left.id.localeCompare(right.id),
    )
    .map((version) => ({
      value: version.id,
      label: `v${version.version_number ?? '?'}${
        version.isPublished ?? Boolean(version.published_at) ? ' · published' : ' · draft'
      }${
        version.id === currentPinnedVersionId ? ' (current pin)' : ''
      }`,
    }));
}

function makeDefaultMapping(triggerInputId: string): SubAutomationTriggerInputMapping {
  return {
    trigger_input_id: triggerInputId,
    data_input: {
      ...DEFAULT_DYNAMIC_DATA_INPUT,
      config: { ...DEFAULT_DYNAMIC_DATA_INPUT.config },
    },
  };
}

export function syncSubAutomationTriggerInputMappings(
  manualTriggerInputs: ManualTriggerInput[],
  currentMappings: SubAutomationTriggerInputMapping[],
) {
  const currentMappingsById = new Map(
    currentMappings.map((mapping) => [mapping.trigger_input_id, mapping]),
  );

  return manualTriggerInputs.map(
    (manualTriggerInput) =>
      currentMappingsById.get(manualTriggerInput.output_id) ??
      makeDefaultMapping(manualTriggerInput.output_id),
  );
}

export function haveDifferentTriggerInputMappingIds(
  left: SubAutomationTriggerInputMapping[],
  right: SubAutomationTriggerInputMapping[],
) {
  if (left.length !== right.length) return true;
  return left.some((mapping, index) => mapping.trigger_input_id !== right[index]?.trigger_input_id);
}
